import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, X } from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  paid: "bg-green-100 text-green-800 border-green-300",
  expired: "bg-red-100 text-red-800 border-red-300",
};

export default function Payments() {
  const { api } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showCreate, setShowCreate] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdPayment, setCreatedPayment] = useState(null);

  const fetchPayments = useCallback(() => {
    api("get", `/admin/payments?status=${filter}`)
      .then((res) => setPayments(res.data.payments || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api, filter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    const iv = setInterval(fetchPayments, 5000);
    return () => clearInterval(iv);
  }, [fetchPayments]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!amount || parseInt(amount) < 100) {
      toast.error("Minimum amount Rp 100");
      return;
    }
    setCreating(true);
    try {
      const res = await api("post", "/admin/payments", {
        amount: parseInt(amount),
        description,
      });
      setCreatedPayment(res.data);
      setShowCreate(false);
      setAmount("");
      setDescription("");
      fetchPayments();
      toast.success("Payment berhasil dibuat!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat payment");
    } finally {
      setCreating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const totalPages = Math.max(1, Math.ceil(payments.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visiblePayments = payments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div data-testid="payments-page" className="space-y-6 sm:space-y-8">
      <div className="flex flex-col gap-4 rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Payments</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">Buat dan kelola pembayaran</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Alur QRIS yang lebih enak dipakai di mobile, tablet, dan desktop.</p>
        </div>
        <button
          data-testid="create-payment-button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
        >
          <Plus size={14} /> Buat Payment
        </button>
      </div>

      {/* QRIS display */}
      {createdPayment && (
        <div data-testid="qris-display" className="mb-8 rounded-3xl border border-slate-950/20 bg-card p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="font-heading text-lg font-bold">QRIS Payment</h3>
            <button data-testid="close-qris" onClick={() => setCreatedPayment(null)} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="shrink-0 rounded-3xl border border-border bg-white p-4">
              <img data-testid="qris-image" src={createdPayment.qris_image} alt="QRIS" className="w-56 h-56" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Payment ID</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="font-mono text-sm">{createdPayment.id}</code>
                  <button onClick={() => copy(createdPayment.id)} className="text-muted-foreground hover:text-foreground"><Copy size={12} /></button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Amount (Bayar)</p>
                <p className="text-2xl font-bold font-heading mt-1">Rp {createdPayment.unique_amount.toLocaleString("id-ID")}</p>
                <p className="text-xs text-muted-foreground">
                  Original: Rp {createdPayment.amount.toLocaleString("id-ID")} + Rp{" "}
                  {createdPayment.unique_amount - createdPayment.amount} (kode unik)
                </p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</p>
                <span className="inline-block px-2 py-0.5 text-xs font-bold uppercase border rounded-sm mt-1 bg-amber-100 text-amber-800 border-amber-300">
                  PENDING
                </span>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Expires</p>
                <p className="text-sm font-mono mt-1">{new Date(createdPayment.expires_at).toLocaleString("id-ID")}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
          <div data-testid="create-payment-modal" className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-2xl">
            <h3 className="font-heading text-xl font-bold mb-4">Buat Payment Baru</h3>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">Jumlah (Rp)</label>
                <input
                  data-testid="payment-amount-input"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-transparent text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="50000"
                  min="100"
                  autoFocus
                />
              </div>
              <div className="mb-6">
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">Deskripsi (Opsional)</label>
                <input
                  data-testid="payment-description-input"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-transparent text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Bayar order #123"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border border-border text-sm font-semibold rounded-sm hover:bg-secondary transition-colors">
                  Batal
                </button>
                <button
                  data-testid="payment-submit-button"
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-gray-800 hover:text-white active:scale-[0.98] transition-colors disabled:opacity-50"
                >
                  {creating ? "Membuat..." : "Buat QRIS"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        {["all", "pending", "paid", "expired"].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setFilter(s)}
            className={`rounded-2xl border px-4 py-2 text-sm font-medium transition-colors ${
              filter === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background/80 text-muted-foreground hover:bg-secondary"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button data-testid="refresh-payments" onClick={fetchPayments} className="ml-auto inline-flex items-center justify-center rounded-2xl border border-border bg-background/80 p-2 text-muted-foreground hover:text-foreground">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">ID</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Memuat...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Belum ada payment</td></tr>
            ) : (
              visiblePayments.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                  <td className="px-4 py-3"><code className="font-mono text-xs">{p.id.slice(0, 12)}...</code></td>
                  <td className="px-4 py-3 text-right font-mono font-medium">Rp {(p.unique_amount || p.amount || 0).toLocaleString("id-ID")}</td>
                  <td className="px-4 py-3 text-muted-foreground truncate max-w-[200px]">{p.description || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase border rounded-sm ${statusColors[p.status] || ""}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(p.created_at).toLocaleString("id-ID")}</td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>

      {!loading && payments.length > 0 && (
        <div className="flex flex-col gap-3 rounded-3xl border border-border/70 bg-card/90 px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Menampilkan {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, payments.length)} dari {payments.length} payment
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Prev
            </button>
            <span className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
